/**
 * Utility function to download files by opening in a new tab
 *
 * Opens the file URL in a new browser tab. The browser will either
 * download the file or display it depending on the file type and browser settings.
 *
 * @param url - The URL of the file to download
 * @returns void
 */
export function downloadFile(url: string): void {
  // Simply open the URL in a new tab
  window.open(url, '_blank');
}

/**
 * Generate a standardized filename for cover art downloads
 *
 * @param artistName - Name of the artist
 * @param trackTitle - Title of the track
 * @param extension - File extension (default: 'jpg')
 * @returns Sanitized filename
 */
export function generateCoverFilename(
  artistName: string,
  trackTitle: string,
  extension: string = 'jpg'
): string {
  // Remove special characters and replace spaces with hyphens
  const sanitize = (str: string) =>
    str.replace(/[^a-zA-Z0-9\s-]/g, '')
       .replace(/\s+/g, '-')
       .toLowerCase();

  const artist = sanitize(artistName);
  const title = sanitize(trackTitle);

  return `${artist}-${title}-cover.${extension}`;
}

/**
 * Generate a standardized filename for audio file downloads
 *
 * @param artistName - Name of the artist
 * @param trackTitle - Title of the track
 * @param extension - File extension (e.g., 'wav', 'mp3', 'flac')
 * @returns Sanitized filename
 */
export function generateAudioFilename(
  artistName: string,
  trackTitle: string,
  extension: string = 'wav'
): string {
  // Remove special characters and replace spaces with hyphens
  const sanitize = (str: string) =>
    str.replace(/[^a-zA-Z0-9\s-]/g, '')
       .replace(/\s+/g, '-')
       .toLowerCase();

  const artist = sanitize(artistName);
  const title = sanitize(trackTitle);

  return `${artist}-${title}.${extension}`;
}

/**
 * Extract file extension from a URL
 *
 * @param url - The URL to extract extension from
 * @returns File extension (without dot) or empty string if not found
 */
export function getFileExtensionFromUrl(url: string): string {
  try {
    const pathname = new URL(url).pathname;
    const match = pathname.match(/\.([a-zA-Z0-9]+)(?:\?|$)/);
    return match ? match[1].toLowerCase() : '';
  } catch {
    return '';
  }
}
